import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { formatPrice as formatAmount, roundCents } from '@/lib/format'
import { useTheme } from '@/lib/hooks'

import { DiscountLine, SummaryLine, SummaryTotal, SummaryWrapper } from './CartSummary.styles'

const CartSummary = ({ total, currency }) => {
  const { t } = useTranslation()
  const themes = useTheme()
  const cart = useSelector(state => state.checkout.cart)
  const loyaltyCard = useSelector(state => state.checkout.loyaltyCard)
  // Same discount formula as finalTotalSelector so the totals always match the Pay button
  const applyDiscount = amount => (loyaltyCard ? amount - amount * 0.10 : amount)
  // Work in rounded cents so gross - discount = Total and Netto + VAT = Total exactly
  const discountedTotal = roundCents(applyDiscount(total))
  const discount = roundCents(total) - discountedTotal

  // Prices are gross; products may carry their own VAT rate (e.g. 8% vs 23% in PL)
  const defaultVatRate = themes.vatRate ?? 0.2
  const vatRates = [...new Set(cart.map(item => item.vatRate ?? defaultVatRate))]
  const vat = cart.reduce((sum, item) => {
    const rate = item.vatRate ?? defaultVatRate
    const gross = applyDiscount(item.qty * item.price)
    return sum + gross - gross / (1 + rate)
  }, 0)
  // Round VAT first so Netto + VAT always adds up to the displayed total
  const roundedVat = roundCents(vat)
  const subtotal = discountedTotal - roundedVat
  const vatLabel = vatRates.length === 1
    ? t('cartPanel.vat', { rate: Math.round(vatRates[0] * 100) })
    : t('cartPanel.vatMixed')

  const formatPrice = (amount) => formatAmount(amount, currency)

  return (
    <SummaryWrapper>
      {loyaltyCard && (
        <DiscountLine>
          <span>{t('cartPanel.loyaltyDiscount')}</span>
          <span>−{formatPrice(discount)}</span>
        </DiscountLine>
      )}
      <SummaryLine>
        <span>{t('cartPanel.subtotal')}</span>
        <span>{formatPrice(subtotal)}</span>
      </SummaryLine>
      <SummaryLine>
        <span>{vatLabel}</span>
        <span>{formatPrice(roundedVat)}</span>
      </SummaryLine>
      <SummaryTotal>
        <span>{t('cartPanel.total')}</span>
        <span>{formatPrice(discountedTotal)}</span>
      </SummaryTotal>
    </SummaryWrapper>
  )
}

export default CartSummary
